import { useContext, useMemo } from 'react';

import { HouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import {
  CLIENT_TWO_VARIABLE_NAMES,
  PRESENTATION_STRINGS,
} from '@/modules/presentation/clientPresentation/presentationString/usePresentationStringFormatter.formatters';

const ALL_VARIABLE_NAMES = Object.keys(PRESENTATION_STRINGS);

/**
 * Returns the list of presentation variable names, filtering out "Client two"
 * variables when the current household has a single grantor.
 *
 * When called outside of a HouseholdDetailsContext (e.g. in a non-household
 * context), all variables are returned.
 */
export function useVariableNames(): string[] {
  const householdDetails = useContext(HouseholdDetailsContext);

  return useMemo(() => {
    // Show all variables when:
    // - No household is loaded (householdId is null) — outside the provider
    // - Client data hasn't resolved yet (primaryClients is null) —
    //   householdId is set from the URL before the query resolves, so we
    //   also check primaryClients to avoid briefly hiding Client two
    //   variables in two-client households during loading
    // - The household has two clients
    if (
      !householdDetails.householdId ||
      !householdDetails.primaryClients ||
      householdDetails.isTwoClientHousehold
    ) {
      return ALL_VARIABLE_NAMES;
    }

    // Single-client household: hide Client two variables
    return ALL_VARIABLE_NAMES.filter(
      (name) => !CLIENT_TWO_VARIABLE_NAMES.has(name)
    );
  }, [
    householdDetails.householdId,
    householdDetails.primaryClients,
    householdDetails.isTwoClientHousehold,
  ]);
}
